import axios from 'axios';
import { toast } from 'sonner';
import { $setConnectionPhase, isServerUnavailableStatus } from '@/entities/connection-entity';
import { $profile } from '@/entities/profile-entity';
import { isSenderApp } from '@/lib/app-mode';
import { formatApiError } from '@/lib/format-api-error';
import { formatSenderApiError } from '@/lib/format-sender-api-error';
import { endPanelSession, isSessionEndMessage } from '@/lib/session-end';
import { endSenderPanelSession } from '@/lib/sender-session-end';

declare module 'axios' {
	export interface AxiosRequestConfig {
		suppressToast?: boolean;
		suppressConnectionOverlay?: boolean;
	}
}

export const api = axios.create({
	baseURL: import.meta.env.VITE_BACK_URL,
	withCredentials: true,
});

function shouldShowConnectionOverlay(error: import('axios').AxiosError): boolean {
	if (error.config?.suppressConnectionOverlay) return false;
	if (!error.response) return true;
	return isServerUnavailableStatus(error.response.status);
}

api.interceptors.response.use(
	response => response,
	(error: unknown) => {
		let message = 'Неизвестная ошибка';

		if (axios.isAxiosError(error)) {
			const status = error.response?.status;
			if (isSenderApp) {
				message =
					formatSenderApiError(error.response?.data, status) ?? error.message ?? message;
			} else {
				message = formatApiError(error.response?.data) ?? error.message ?? message;
			}

			if (status === 403 && $profile.getState() && isSessionEndMessage(message)) {
				if (isSenderApp) {
					endSenderPanelSession({ message });
				} else {
					endPanelSession({ message });
				}
				return Promise.reject(error);
			}

			if (shouldShowConnectionOverlay(error)) {
				$setConnectionPhase('unavailable');
				return Promise.reject(error);
			}

			if (!error.config?.suppressToast) {
				toast.error(message);
			}
		}

		return Promise.reject(error);
	},
);
